import type {SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import {UNSET_ID, type IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import {useCallback} from 'react'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import {useAnalytics} from '@github-ui/use-analytics'

import {useUpdateIssueIssueTypeBulkMutation} from './mutations/use-update-issue-issue-type-bulk-mutation'
import {IssueTypePicker, type IssueTypePickerProps} from './IssueTypePicker'

export type BulkIssueIssueTypePickerProps = Omit<IssueTypePickerProps, 'onSelectionChange'> &
  SharedBulkActionsItemPickerProps &
  Parameters<typeof useUpdateIssueIssueTypeBulkMutation>[0]

export function BulkIssueIssueTypePicker({
  onError,
  onCompleted,
  onMutate,
  issuesToActOn,
  activeIssueType,
  ...rest
}: BulkIssueIssueTypePickerProps) {
  const {mutate: bulkUpdateMutate} = useUpdateIssueIssueTypeBulkMutation({onMutate})
  const {sendAnalyticsEvent} = useAnalytics()
  const onSelectionChange = useCallback(
    (selectedIssueType: IssueTypePickerIssueType[]) => {
      const issueTypeId = selectedIssueType.length === 1 ? selectedIssueType[0]?.id : null
      if (!issueTypeId || issueTypeId === activeIssueType?.id) return
      const ids = [...issuesToActOn]
      let input: Parameters<typeof onMutate>[0]
      // Check if user selected the "No type" option to clear the issue type
      if (issueTypeId === SPECIAL_VALUES.noTypeData.id || issueTypeId === UNSET_ID) {
        input = {ids, clearIssueType: true as const}
      } else {
        input = {ids, issueTypeId, issueType: selectedIssueType[0]}
      }

      sendAnalyticsEvent('issues_index.bulk_action_issue_type', 'BULK_ISSUE_ISSUE_TYPE_PICKER', {
        initialIssueTypeId: activeIssueType?.id ?? '',
        issueTypeId: issueTypeId === UNSET_ID ? '' : (issueTypeId ?? ''),
        issueIds: [...issuesToActOn],
      })

      bulkUpdateMutate(input, {
        onSuccess: response => onCompleted?.(response.data.updateIssuesBulk.jobId),
        onError,
      })
    },
    [bulkUpdateMutate, issuesToActOn, activeIssueType?.id, onCompleted, onError, sendAnalyticsEvent],
  )

  return <IssueTypePicker onSelectionChange={onSelectionChange} activeIssueType={activeIssueType} showUnset {...rest} />
}
